/**
 * Credit notes linked to invoices (1.58, decisions of 10/10/2026): a credit
 * note, issued or received, closes in part or in full one or more invoices of
 * the same subject and cycle. The link lowers what is left on the invoice and
 * on the note by the same amount, VAT included (on the net due, as collections
 * and payments): no bank movement. What of the note is not used stays open,
 * to be refunded through the bank or linked later.
 */
import { z } from 'zod';
import { formatMoney, fromCents, toCents } from './money.js';
import { textCitesNumber } from './reconciliation.js';
import { nameKey, normaliseTaxId } from './tax-ids.js';
import { idSchema } from './validation.js';
/** At most this many invoices in one link of a credit note. */
export const CREDIT_LINK_MAX = 20;
const positiveMoney = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value)))
    .refine((value) => toCents(value) > 0, { message: 'Importo non valido' });
/** The invoices a credit note closes, each with the amount taken off it (always positive). */
export const creditLinkSchema = z.object({
    links: z
        .array(z.object({ invoiceId: idSchema, amount: positiveMoney }))
        .min(1, { message: 'Scegli almeno una fattura' })
        .max(CREDIT_LINK_MAX)
        .refine((links) => new Set(links.map((link) => link.invoiceId)).size === links.length, {
        message: 'Una fattura compare due volte',
    }),
});
/** A tax id to compare: «IT01234567890» and «01234567890» are the same. */
function taxKey(value) {
    const key = value ? normaliseTaxId(value) : '';
    return /^IT\d{11}$/.test(key) ? key.slice(2) : key;
}
/**
 * Whether two documents are of the same counterparty: the same subject when
 * both have one; otherwise (2.12: an item of the history fixed before its
 * ledger was linked to the Anagrafica) the same VAT number or tax code, or
 * the same name when neither has one.
 */
export function sameCounterparty(a, b) {
    if (a.subjectId && b.subjectId)
        return a.subjectId === b.subjectId;
    const ids = (party) => [taxKey(party.counterpartyVat), taxKey(party.counterpartyTaxCode)].filter(Boolean);
    const left = ids(a);
    const right = ids(b);
    if (left.length && right.length)
        return left.some((id) => right.includes(id));
    return nameKey(a.counterpartyName) === nameKey(b.counterpartyName);
}
function tagValue(block, tag) {
    const match = new RegExp(`<(?:\\w+:)?${tag}>\\s*([^<]*?)\\s*</(?:\\w+:)?${tag}>`).exec(block);
    return match ? decodeXml(match[1]) : null;
}
function decodeXml(value) {
    return value
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&amp;/g, '&');
}
/**
 * The invoices a credit note corrects, as its electronic invoice cites them
 * (DatiGenerali › DatiFattureCollegate: IdDocumento and Data).
 */
export function citedInvoices(xml) {
    const cited = [];
    const blocks = xml.matchAll(/<(?:\w+:)?DatiFattureCollegate>([\s\S]*?)<\/(?:\w+:)?DatiFattureCollegate>/g);
    for (const [, block] of blocks) {
        const number = tagValue(block, 'IdDocumento');
        if (!number)
            continue;
        const date = tagValue(block, 'Data');
        cited.push({ number, date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null });
    }
    return cited;
}
/** Whether the invoice is among those cited by the XML: same number, and same date when given. */
export function isCitedInvoice(invoice, cited) {
    return cited.some((item) => (!item.date || item.date === invoice.date) &&
        (sameNumber(item.number, invoice.number) ||
            textCitesNumber(`FT ${item.number}`, invoice.number)));
}
function sameNumber(a, b) {
    const key = (value) => value.toUpperCase().replace(/\s+/g, '');
    return key(a) === key(b);
}
/**
 * The candidates of a credit note, the cited first (by date), then the others
 * from the newest; the cited take an amount each, the oldest first, as long as
 * the note has something left.
 */
export function creditCandidates(noteResidual, invoices) {
    let available = Math.abs(toCents(noteResidual));
    const cited = invoices
        .filter((invoice) => invoice.cited)
        .sort((a, b) => a.date.localeCompare(b.date) || a.number.localeCompare(b.number, 'it'));
    const others = invoices
        .filter((invoice) => !invoice.cited)
        .sort((a, b) => b.date.localeCompare(a.date) || b.number.localeCompare(a.number, 'it'));
    return [
        ...cited.map((invoice) => {
            const amount = Math.min(available, Math.abs(toCents(invoice.residual)));
            available -= amount;
            return { ...invoice, proposed: amount > 0 ? fromCents(amount) : null };
        }),
        ...others.map((invoice) => ({ ...invoice, proposed: null })),
    ];
}
/**
 * What is wrong with a link, or null: no invoice gets more than what is left on
 * it, the note gives no more than what is left on it.
 */
export function creditLinkError(noteResidual, links) {
    for (const link of links) {
        if (toCents(link.amount) > Math.abs(toCents(link.residual))) {
            return `Sulla fattura ${link.number} restano solo ${formatMoney(fromCents(Math.abs(toCents(link.residual))))}`;
        }
    }
    const total = links.reduce((sum, link) => sum + toCents(link.amount), 0);
    const available = Math.abs(toCents(noteResidual));
    if (total > available) {
        return `La nota di credito ha ancora ${formatMoney(fromCents(available))} da usare, non ${formatMoney(fromCents(total))}`;
    }
    return null;
}
//# sourceMappingURL=credit-notes.js.map